/**
 * Chat Analytics Events
 * Standardized analytics tracking for AgenticChatLauncher component
 * Integrates with Google Tag Manager and GA4 for engagement measurement
 */

/**
 * Detect device type for analytics
 * @returns {string} Device type (mobile, tablet, desktop)
 */
export function getDeviceType() {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTablet = /ipad|android(?!.*mobile)|tablet/i.test(userAgent);
  
  if (isTablet) return 'tablet';
  if (isMobile) return 'mobile';
  return 'desktop';
}

/**
 * Get current page path for analytics
 * @returns {string} Current page path
 */
export function getCurrentPagePath() {
  return window.location.pathname;
}

/**
 * Get current timestamp in ISO format
 * @returns {string} ISO timestamp
 */
export function getCurrentTimestamp() {
  return new Date().toISOString();
}

/**
 * Send event to Google Tag Manager
 * @param {string} eventName - Name of the event
 * @param {Object} parameters - Event parameters
 */
export function sendGTMEvent(eventName, parameters = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    const eventData = {
      ...parameters,
      timestamp: getCurrentTimestamp(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    };

    // Send to GTM
    window.gtag('event', eventName, eventData);
    
    // Also push to dataLayer for debugging
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData
      });
    }

    console.log(`📊 Analytics Event: ${eventName}`, eventData);
  } else {
    console.warn('GTM not available - event not sent:', eventName, parameters);
  }
}

/**
 * Chat Analytics Event Handlers
 */
export class ChatAnalytics {
  constructor() {
    this.chatStartTime = null;
    this.messageCount = 0;
    this.totalCharactersSent = 0;
  }

  /**
   * Track chat opening
   * @param {Object} options - Additional parameters
   */
  trackChatOpen(options = {}) {
    this.chatStartTime = Date.now();
    this.messageCount = 0;
    this.totalCharactersSent = 0;

    const eventParams = {
      page_path: getCurrentPagePath(),
      device_type: getDeviceType(),
      chat_session_id: this.generateSessionId(),
      referrer: document.referrer || 'direct',
      ...options
    };

    sendGTMEvent('chat_open', eventParams);
    
    // Set session storage for session tracking
    sessionStorage.setItem('chat_session_start', this.chatStartTime.toString());
    sessionStorage.setItem('chat_session_id', eventParams.chat_session_id);
  }

  /**
   * Track message sent by user
   * @param {string} message - The message text
   * @param {Object} options - Additional parameters
   */
  trackMessageSent(message, options = {}) {
    this.messageCount++;
    this.totalCharactersSent += message.length;

    const eventParams = {
      message_length: message.length,
      page_path: getCurrentPagePath(),
      device_type: getDeviceType(),
      chat_session_id: sessionStorage.getItem('chat_session_id') || 'unknown',
      message_number: this.messageCount,
      total_characters_sent: this.totalCharactersSent,
      message_type: 'user_input',
      has_quick_reply: this.isQuickReply(message),
      ...options
    };

    sendGTMEvent('chat_message_sent', eventParams);
  }

  /**
   * Track chat closing
   * @param {Object} options - Additional parameters
   */
  trackChatClose(options = {}) {
    const duration = this.chatStartTime ? Math.round((Date.now() - this.chatStartTime) / 1000) : 0;

    const eventParams = {
      duration: duration,
      page_path: getCurrentPagePath(),
      device_type: getDeviceType(),
      chat_session_id: sessionStorage.getItem('chat_session_id') || 'unknown',
      messages_sent: this.messageCount,
      total_characters_sent: this.totalCharactersSent,
      engagement_level: this.calculateEngagementLevel(duration, this.messageCount),
      ...options
    };

    sendGTMEvent('chat_close', eventParams);
    
    // Clear session data
    sessionStorage.removeItem('chat_session_start');
    sessionStorage.removeItem('chat_session_id');
    
    // Reset counters
    this.chatStartTime = null;
    this.messageCount = 0;
    this.totalCharactersSent = 0;
  }

  /**
   * Track quick reply button clicks
   * @param {string} quickReplyText - The quick reply text
   * @param {Object} options - Additional parameters
   */
  trackQuickReply(quickReplyText, options = {}) {
    const eventParams = {
      quick_reply_text: quickReplyText,
      page_path: getCurrentPagePath(),
      device_type: getDeviceType(),
      chat_session_id: sessionStorage.getItem('chat_session_id') || 'unknown',
      ...options
    };

    sendGTMEvent('chat_quick_reply', eventParams);
  }

  /**
   * Track chat minimization (if different from close)
   * @param {Object} options - Additional parameters
   */
  trackChatMinimize(options = {}) {
    const duration = this.chatStartTime ? Math.round((Date.now() - this.chatStartTime) / 1000) : 0;

    const eventParams = {
      duration_before_minimize: duration,
      page_path: getCurrentPagePath(),
      device_type: getDeviceType(),
      chat_session_id: sessionStorage.getItem('chat_session_id') || 'unknown',
      messages_sent: this.messageCount,
      ...options
    };

    sendGTMEvent('chat_minimize', eventParams);
  }

  /**
   * Generate unique session ID
   * @returns {string} Session ID
   */
  generateSessionId() {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Check if message is a quick reply
   * @param {string} message - The message text
   * @returns {boolean} True if it's a quick reply
   */
  isQuickReply(message) {
    const commonQuickReplies = ["Cloud Solutions", "AI Integration", "Mobile Apps"];
    return commonQuickReplies.includes(message);
  }

  /**
   * Calculate engagement level based on duration and messages
   * @param {number} duration - Chat duration in seconds
   * @param {number} messageCount - Number of messages sent
   * @returns {string} Engagement level
   */
  calculateEngagementLevel(duration, messageCount) {
    if (duration < 10 && messageCount === 0) return 'bounce';
    if (duration < 30 && messageCount <= 1) return 'low';
    if (duration < 120 && messageCount <= 3) return 'medium';
    if (duration >= 120 || messageCount > 3) return 'high';
    return 'unknown';
  }

  /**
   * Get current chat session data
   * @returns {Object} Session data
   */
  getSessionData() {
    return {
      sessionId: sessionStorage.getItem('chat_session_id'),
      startTime: sessionStorage.getItem('chat_session_start'),
      messageCount: this.messageCount,
      totalCharactersSent: this.totalCharactersSent,
      duration: this.chatStartTime ? Math.round((Date.now() - this.chatStartTime) / 1000) : 0
    };
  }
}

/**
 * Global chat analytics instance
 */
export const chatAnalytics = new ChatAnalytics();

/**
 * Convenience functions for common chat events
 */
export const trackChatOpen = (options) => chatAnalytics.trackChatOpen(options);
export const trackMessageSent = (message, options) => chatAnalytics.trackMessageSent(message, options);
export const trackChatClose = (options) => chatAnalytics.trackChatClose(options);
export const trackQuickReply = (text, options) => chatAnalytics.trackQuickReply(text, options);
export const trackChatMinimize = (options) => chatAnalytics.trackChatMinimize(options);

/**
 * Enhanced mobile tracking utilities
 */
export const MobileTrackingUtils = {
  /**
   * Detect if user is on mobile device
   */
  isMobileDevice() {
    return getDeviceType() === 'mobile';
  },

  /**
   * Track mobile-specific interactions
   */
  trackMobileInteraction(interactionType, data = {}) {
    if (this.isMobileDevice()) {
      sendGTMEvent('mobile_chat_interaction', {
        interaction_type: interactionType,
        page_path: getCurrentPagePath(),
        device_type: 'mobile',
        ...data
      });
    }
  },

  /**
   * Track keyboard events on mobile
   */
  trackKeyboardEvent(eventType, data = {}) {
    if (this.isMobileDevice()) {
      sendGTMEvent('mobile_keyboard_event', {
        keyboard_event: eventType,
        page_path: getCurrentPagePath(),
        device_type: 'mobile',
        ...data
      });
    }
  },

  /**
   * Track touch target size compliance
   */
  trackTouchTargetCompliance(elementId, size, data = {}) {
    if (this.isMobileDevice()) {
      sendGTMEvent('touch_target_compliance', {
        element_id: elementId,
        target_size: size,
        is_compliant: size >= 44,
        page_path: getCurrentPagePath(),
        device_type: 'mobile',
        ...data
      });
    }
  }
};

const ChatAnalyticsExports = {
  ChatAnalytics,
  chatAnalytics,
  trackChatOpen,
  trackMessageSent,
  trackChatClose,
  trackQuickReply,
  trackChatMinimize,
  sendGTMEvent,
  getDeviceType,
  getCurrentPagePath,
  MobileTrackingUtils
};

export default ChatAnalyticsExports;